import React from 'react';
import { connect } from 'react-redux';
import cd from 'debug';
import { compose } from 'redux';
import { RouteComponentProps, withRouter } from 'react-router';
import core, { App, Viewport } from '@gin-betting/core';
import matchList, { Pagination } from '@gin-betting/match-list';

import type { State } from 'types';
import { getTimeIntervalByUnit } from 'utils';
import {
    withBettingReadyIndicatorContext,
    BettingReadyIndicatorContextProps,
} from 'modules/betting-ready-indicator';
import ResultsMatchList from './ResultsMatchList';
import PaginationWrapper from '../PaginationWrapper';

const debug = cd('app:match-list:components:Results');

const {
    utils: { isFetchFinished },
} = core;

const {
    viewport: {
        selectors: { makeViewportSelector },
    },
    utils: { extendSearchString, getAppliedFilters },
} = core;

const {
    selectors: { selectCategoryByLocation },
    pagination: {
        actions: { dropPaginationState, setMinAndMaxPages },
        hocs: { withPagination },
        selectors: {
            selectPaginationByLocation,
            selectDirection,
            selectIsInitialLoad,
        },
    },
} = matchList;

interface OwnProps {}

interface MappedProps extends Pagination.NeededPaginationProps {
    lessThanOrEqual: Viewport.Breakpoints;
}

interface MappedActions {
    dropPaginationState: typeof dropPaginationState;
    setMinAndMaxPages: typeof setMinAndMaxPages;
}

type Props = OwnProps &
    MappedProps &
    Partial<Pagination.ReturnedPaginationProps> &
    MappedActions &
    BettingReadyIndicatorContextProps &
    RouteComponentProps;

class Results extends React.Component<Props> {
    componentDidMount() {
        const { dateFrom } = getAppliedFilters();

        if (dateFrom) return;

        extendSearchString(
            'dateFrom',
            getTimeIntervalByUnit({
                interval: -72,
                unit: 'hour',
            })
        );
    }

    componentDidUpdate() {
        const {
            readyIndicator,
            categoryData: { fetchStatus },
        } = this.props;

        if (isFetchFinished(fetchStatus)) {
            readyIndicator.setReady();
        }
    }

    render() {
        debug('render', this.props);

        const {
            lessThanOrEqual,
            categoryData,
            hasNext,
            hasMore,
            isUp,
            matchIdsWithVisibilityFlags,
            visibilityInitRef,
            handleVisibilityChange,
            loadMoreWithPagination,
        } = this.props;

        const { matchIds, fetchStatus } = categoryData;

        const isSmall = lessThanOrEqual.small;
        const isMedium = lessThanOrEqual.medium;

        return (
            <PaginationWrapper
                hasNext={hasNext}
                pollerName={App.PollerName.Results}
                fetchStatus={fetchStatus}
                matchIdsSize={matchIds.size}
                setMinAndMaxPages={this.props.setMinAndMaxPages}
                /* for pagination */
                hasMore={hasMore}
                isUp={isUp}
                dropPaginationState={this.props.dropPaginationState}
                loadMoreWithPagination={loadMoreWithPagination}
                isMedium={isMedium}
            >
                <ResultsMatchList
                    isSmall={isSmall}
                    matchIds={matchIdsWithVisibilityFlags || matchIds}
                    /* for pagination */
                    visibilityInitRef={visibilityInitRef}
                    handleVisibilityChange={handleVisibilityChange}
                />
            </PaginationWrapper>
        );
    }
}

const makeMapStateToProps = (
    state: State,
    ownProps: OwnProps & RouteComponentProps
): MappedProps => {
    const {
        hasNext,
        minPage,
        currentPage,
        maxPage,
        limitPerPage,
    } = selectPaginationByLocation(state, ownProps);
    const categoryData = selectCategoryByLocation(state, ownProps);

    return {
        lessThanOrEqual: makeViewportSelector('lessThanOrEqual')(
            state,
            ownProps
        ),
        categoryData,
        /* for pagination */
        hasNext,
        pages: { minPage, currentPage, maxPage, limitPerPage },
        direction: selectDirection(state),
        isLoadInitial: selectIsInitialLoad(state),
    };
};

const mapDispatchToProps = {
    dropPaginationState,
    setMinAndMaxPages,
};

export default compose<React.ComponentClass<OwnProps>>(
    withRouter,
    connect<MappedProps, {}, OwnProps>(makeMapStateToProps, mapDispatchToProps),
    withPagination,
    withBettingReadyIndicatorContext
)(Results);
